import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { COUNTRY_CONFIGS, formatCurrency, getCurrencySymbol } from '../../utils/formatters';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    phone: ['M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z'],
    mapPin: ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z', 'M12 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6z'],
    calendar: ['M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z', 'M16 2v4', 'M8 2v4', 'M3 10h18'],
    clock: ['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', 'M12 6v6l4 2'],
    truck: ['M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M5 17H3V7c0-1.1.9-2 2-2h11v12h-2', 'M19 17h2v-6l-3-4h-2v7h4'],
    check: ['M20 6L9 17l-5-5'],
    loader: ['M12 2v4', 'M12 18v4', 'M4.93 4.93l2.83 2.83', 'M16.24 16.24l2.83 2.83', 'M2 12h4', 'M18 12h4', 'M4.93 19.07l2.83-2.83', 'M16.24 7.76l2.83-2.83']
};

const getCurrencyCountry = (operator) => {
    if (!operator) return 'India';
    if (operator.defaultCurrency) {
        const match = operator.defaultCurrency.match(/\(([^)]+)\)/);
        if (match && match[1]) {
            const code = match[1];
            if (code === 'INR') return 'India';
            if (code === 'KWD') return 'Kuwait';
            if (code === 'AED') return 'United Arab Emirates';
            if (code === 'USD') return 'United States';
            if (code === 'GBP') return 'United Kingdom';
        }
    }
    return operator.country || 'India';
};

function LocationAutocomplete({ value, onChange, placeholder, icon, iconColor, borderColor, required }) {
    const [suggestions, setSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const timeoutRef = useRef(null);

    const fetchSuggestions = async (query) => {
        if (!query || query.length < 3) {
            setSuggestions([]);
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`);
            const data = await res.json();
            setSuggestions(data.map(d => d.display_name));
        } catch { }
        setLoading(false);
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        onChange(val);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            fetchSuggestions(val);
        }, 500);
        setShowDropdown(true);
    };

    return (
        <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: iconColor || '#00B2A9' }}>
                <Icon d={icon} size={18} />
            </div>
            <input
                className="md-input"
                style={{ paddingLeft: '40px', height: '48px', borderRadius: '12px', borderColor: borderColor || '#e6f0fa', width: '100%', boxSizing: 'border-box' }}
                placeholder={placeholder}
                value={value}
                onChange={handleInputChange}
                onFocus={() => { if (suggestions.length) setShowDropdown(true); }}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                required={required}
                autoComplete="off"
            />
            {showDropdown && (suggestions.length > 0 || loading) && (
                <ul style={{ 
                    position: 'absolute', top: '100%', left: 0, right: 0, 
                    background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, 
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, 
                    listStyle: 'none', padding: 0, margin: '4px 0 0 0', 
                    maxHeight: 250, overflowY: 'auto' 
                }}>
                    {loading && <li style={{ padding: '10px 16px', fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>Searching...</li>}
                    {!loading && suggestions.map((s, i) => (
                        <li 
                            key={i} 
                            style={{ padding: '10px 16px', fontSize: 13, color: '#334155', cursor: 'pointer', borderBottom: i < suggestions.length - 1 ? '1px solid #f1f5f9' : 'none', wordBreak: 'break-word', textAlign: 'left' }}
                            onMouseDown={() => {
                                onChange(s);
                                setShowDropdown(false);
                            }}
                            onMouseEnter={e => e.target.style.background = '#f8fafc'}
                            onMouseLeave={e => e.target.style.background = 'white'}
                        >
                            {s}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function AdhocTrips({ user, operatorDetails }) {
    const currencyCountry = getCurrencyCountry(operatorDetails);
    const currencySymbol = getCurrencySymbol(operatorDetails?.country || currencyCountry);

    const [form, setForm] = useState({
        passengers: [{ passengerName: '', passengerPhone: '', passengerEmail: '' }],
        passengerName: '',
        passengerPhone: '',
        countryCode: COUNTRY_CONFIGS[currencyCountry]?.phoneCode || '+91',
        passengerEmail: '',
        pickupLocation: '',
        dropLocation: '',
        tripDate: new Date().toISOString().split('T')[0],
        pickupTime: '',
        dropTime: '',
        vehicleId: '',
        driverId: '',
        vendorId: '',
        isVendorMode: false,
        amount: '',
        tripType: 'on_demand',
        billingType: 'Ad-hoc',
        tripFrequency: 'One Time',
        status: 'Scheduled',
        clientId: ''
    });

    const [showPriceModal, setShowPriceModal] = useState(null);
    const [showAssignModal, setShowAssignModal] = useState(null);
    const [showAllTrips, setShowAllTrips] = useState(false);
    const [activeTab, setActiveTab] = useState('adhoc');

    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [vendorFleet, setVendorFleet] = useState([]);
    const [adhocTrips, setAdhocTrips] = useState([]);
    const [clients, setClients] = useState([]);
    const [allPassengers, setAllPassengers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [success, setSuccess] = useState(null);
    const [mappings, setMappings] = useState([]);

    const operatorId = user?.operatorId || user?._id;

    useEffect(() => {
        fetchResources();
    }, []);



    const fetchResources = async () => {
        try {
            const [vRes, dRes, vnRes, tRes, vfRes, cRes, pRes, mRes] = await Promise.all([
                axios.get(`${API}/vehicles/${operatorId}`),
                axios.get(`${API}/drivers/${operatorId}`),
                axios.get(`${API}/vendors/${operatorId}`),
                axios.get(`${API}/trips/${operatorId}`),
                axios.get(`${API}/vendor-fleet/${operatorId}`),
                axios.get(`${API}/clients/${operatorId}`),
                axios.get(`${API}/passengers/${operatorId}`),
                axios.get(`${API}/driver-vehicle-mappings/${operatorId}`)
            ]);
            setVehicles(vRes.data);
            setDrivers(dRes.data);
            setVendors(vnRes.data);
            setVendorFleet(vfRes.data);
            setAdhocTrips(tRes.data.filter(t => t.tripType === 'on_demand'));
            setClients(cRes.data);
            setAllPassengers(pRes.data);
            setMappings(mRes.data);
        } catch (err) {
            console.error("Error fetching resources:", err);
        } finally {
            setFetchingData(false);
        }
    };

    const handleVehicleChange = (vId) => {
        if (form.isVendorMode) {
            // Selected a vendor fleet vehicle
            setForm(prev => ({ ...prev, vehicleId: vId }));
        } else {
            // Selected an internal vehicle - auto assign driver/vendor if linked
            const vehicle = vehicles.find(v => v._id === vId);
            const activeMapping = mappings.find(m => (m.vehicleId?._id === vId || m.vehicleId === vId) && m.isActive);
            if (vehicle) {
                setForm(prev => ({
                    ...prev,
                    vehicleId: vId,
                    driverId: activeMapping ? (activeMapping.driverId?._id || activeMapping.driverId) : (vehicle.driverId?._id || vehicle.driverId || prev.driverId),
                    vendorId: vehicle.vendorId?._id || vehicle.vendorId || ''
                }));
            } else {
                setForm(prev => ({ ...prev, vehicleId: vId }));
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.passengers[0].passengerName || !form.passengers[0].passengerPhone || !form.pickupLocation || !form.dropLocation || !form.tripDate || !form.pickupTime || !form.vehicleId) {
            alert("Please fill all required fields.");
            return;
        }

        setLoading(true);
        try {
            const passengerName = form.passengers.map(p => p.passengerName).join(', ');
            const passengerPhone = form.passengers.map(p => `${form.countryCode}${p.passengerPhone}`).join(', ');
            const passengerEmail = form.passengers.map(p => p.passengerEmail).filter(Boolean).join(', ');
            const payload = {
                ...form,
                operatorId,
                passengerName,
                passengerPhone,
                passengerEmail,
                startTime: form.pickupTime,
                endTime: form.dropTime || ''
            };

            if (payload.clientId) {
                payload.status = 'PriceProposed';
                payload.proposedPrice = payload.amount ? Number(payload.amount) : 0;
            }

            await axios.post(`${API}/trips`, payload);
            setSuccess(true);
            setForm({
                passengers: [{ passengerName: '', passengerPhone: '', passengerEmail: '' }],
                passengerName: '',
                passengerPhone: '',
                countryCode: COUNTRY_CONFIGS[currencyCountry]?.phoneCode || '+91',
                passengerEmail: '',
                pickupLocation: '',
                dropLocation: '',
                tripDate: new Date().toISOString().split('T')[0],
                pickupTime: '',
                dropTime: '',
                vehicleId: '',
                driverId: '',
                vendorId: '',
                isVendorMode: false,
                amount: '',
                tripType: 'on_demand',
                billingType: 'Ad-hoc',
                tripFrequency: 'One Time',
                status: 'Scheduled',
                clientId: ''
            });
            fetchResources(); // Refresh list
            setTimeout(() => setSuccess(null), 5000);
        } catch (err) {
            console.error("Error creating adhoc trip:", err);
            alert("Failed to create trip. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (tripId, status, additionalData = {}) => {
        try {
            await axios.put(`${API}/trips/${tripId}`, { status, ...additionalData });
            fetchResources();
            if (status === 'Scheduled') setShowAssignModal(null);
        } catch (err) {
            console.error("Error updating trip status:", err);
            alert("Failed to update trip status.");
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Requested': return { bg: '#fef3c7', color: '#92400e', label: 'Requested' };
            case 'PriceProposed': return { bg: '#e0f0ff', color: '#5b21b6', label: 'Price Proposed' };
            case 'ClientAccepted': return { bg: '#d1fae5', color: '#065f46', label: 'Client Accepted' };
            case 'ClientRejected': return { bg: '#fee2e2', color: '#991b1b', label: 'Client Rejected' };
            case 'ClientSuggested': return { bg: '#fef3c7', color: '#b45309', label: 'Counter Offer' };
            case 'Scheduled': return { bg: '#dcfce7', color: '#166534', label: 'Scheduled' };
            case 'Rejected': return { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' };
            case 'Completed': return { bg: '#e0f2fe', color: '#075985', label: 'Completed' };
            case 'Cancelled': return { bg: '#f1f5f9', color: '#475569', label: 'Cancelled' };
            default: return { bg: '#f1f5f9', color: '#475569', label: status };
        }
    };


    const operatorTrips = adhocTrips.filter(t => t.source !== 'Client' && t.status !== 'Requested');
    const clientRequests = adhocTrips.filter(t => t.source === 'Client' || t.status === 'Requested');
    const displayedTrips = activeTab === 'adhoc' ? operatorTrips : clientRequests;

    if (fetchingData) {

        return <div style={{ padding: 100, textAlign: 'center', color: '#64748b' }}>
            <div className="spinner" style={{ margin: '0 auto 20px', width: 40, height: 40, border: '3px solid #f3f4f6', borderTopColor: '#00B2A9', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            Initializing Adhoc Portal...
        </div>;
    }

    const filteredVendorFleet = vendorFleet.filter(v => v.vendorId?._id === form.vendorId || v.vendorId === form.vendorId);

    return (
        <div className="md-page">
            <div className="md-header">
                <div>
                    <h1 className="md-title">Adhoc Trips</h1>
                    <p className="md-subtitle">Create and manage immediate trip requirements.</p>
                </div>
            </div>

            <div className="adhoc-grid">
                {/* Left: Form Card */}
                <div className="md-panel" style={{ padding: '35px', borderRadius: '24px', boxShadow: '0 20px 50px rgba(0,0,0,0.05)', border: '1px solid rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)' }}>
                    <form onSubmit={handleSubmit}>
                        <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#063A5C', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ padding: '8px', background: 'linear-gradient(135deg, #00B2A9, #a855f7)', borderRadius: '12px', color: '#fff' }}>
                                <Icon d={icons.truck} size={20} />
                            </div>
                            Create New Adhoc Trip
                        </h2>

                        {success && (
                            <div style={{
                                marginBottom: '25px', padding: '15px 20px', borderRadius: '16px',
                                background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534',
                                display: 'flex', alignItems: 'center', gap: '12px', animation: 'fadeIn 0.4s ease'
                            }}>
                                <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#22c55e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Icon d={icons.check} size={14} />
                                </div>
                                <span style={{ fontWeight: 600 }}>Trip successfully scheduled!</span>
                            </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            {/* Client & Existing Passenger Selection */}
                            <div className="md-form-group" style={{ gridColumn: 'span 2', background: '#f8fafc', padding: '15px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                                <label className="md-label" style={{ color: '#0369a1' }}>Client (Optional)</label>
                                <select
                                    className="md-input"
                                    style={{ height: '48px', borderRadius: '12px', border: '1px solid #bae6fd' }}
                                    value={form.clientId}
                                    onChange={e => setForm({ ...form, clientId: e.target.value })}
                                >
                                    <option value="">-- No Client (Direct / B2C) --</option>
                                    {clients.map(c => (
                                        <option key={c._id} value={c._id}>{c.companyName}</option>
                                    ))}
                                </select>
                                
                                {form.clientId && (
                                    <div style={{ marginTop: '15px' }}>
                                        <label className="md-label" style={{ color: '#11427D' }}>Select Existing Passengers</label>
                                        <select 
                                            className="md-input" 
                                            style={{ height: '48px', borderRadius: '12px', border: '1px solid #c3dcf5' }}
                                            onChange={e => {
                                                if(!e.target.value) return;
                                                const p = allPassengers.find(x => x._id === e.target.value);
                                                if (p) {
                                                    const phone = p.contactNumber || p.phone || '';
                                                    const exists = form.passengers.find(x => x.passengerPhone === phone);
                                                    if (!exists) {
                                                        const newP = { 
                                                            passengerName: p.name, 
                                                            passengerPhone: phone, 
                                                            passengerEmail: p.email || '' 
                                                        };
                                                        const isFirstEmpty = form.passengers.length === 1 && !form.passengers[0].passengerName;
                                                        
                                                        setForm(prev => {
                                                            const updatedP = isFirstEmpty ? [newP] : [...prev.passengers, newP];
                                                            return {
                                                                ...prev,
                                                                passengers: updatedP
                                                            };
                                                        });
                                                    }
                                                }
                                                e.target.value = "";
                                            }}
                                        >
                                            <option value="">-- Choose to auto-fill details --</option>
                                            {allPassengers.filter(p => p.clientId?._id === form.clientId || p.clientId === form.clientId).map(p => (
                                                <option key={p._id} value={p._id}>{p.name} ({p.contactNumber || p.phone || ''})</option>
                                            ))}
                                        </select>
                                        <p style={{ fontSize: '11.5px', color: '#64748b', marginTop: '6px', fontWeight: 500 }}>
                                            Select a passenger to add them below. You can still manually type details for new passengers.
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Passenger Details */}
                            {form.passengers.map((p, idx) => (
                                <div key={idx} style={{ gridColumn: 'span 2', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', padding: '15px', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0', position: 'relative' }}>
                                    {idx > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setForm({ ...form, passengers: form.passengers.filter((_, i) => i !== idx) })}
                                            style={{ position: 'absolute', top: '10px', right: '10px', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '8px', padding: '4px 8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                                        >
                                            ✕ Remove
                                        </button>
                                    )}
                                    <div className="md-form-group">
                                        <label className="md-label">Passenger Name*</label>
                                        <div style={{ position: 'relative' }}>
                                            <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                                <Icon d={icons.user} size={16} />
                                            </div>
                                            <input
                                                className="md-input"
                                                style={{ paddingLeft: '40px', height: '48px', borderRadius: '12px' }}
                                                placeholder="Full Name"
                                                value={p.passengerName}
                                                onChange={e => {
                                                    const newP = [...form.passengers];
                                                    newP[idx].passengerName = e.target.value;
                                                    setForm({ ...form, passengers: newP });
                                                }}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="md-form-group">
                                        <label className="md-label">Phone Number*</label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <select
                                                className="md-input"
                                                style={{ width: '90px', height: '48px', borderRadius: '12px', textAlign: 'center', padding: '0 5px' }}
                                                value={form.countryCode}
                                                onChange={e => setForm({ ...form, countryCode: e.target.value })}
                                            >
                                                {(operatorDetails?.country ? [COUNTRY_CONFIGS[operatorDetails.country]].filter(Boolean) : Object.values(COUNTRY_CONFIGS)).map(config => (
                                                    <option key={config.phoneCode} value={config.phoneCode}>
                                                        {config.phoneCode}
                                                    </option>
                                                ))}
                                            </select>
                                            <div style={{ position: 'relative', flex: 1 }}>
                                                <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                                    <Icon d={icons.phone} size={16} />
                                                </div>
                                                <input
                                                    className="md-input"
                                                    style={{ paddingLeft: '40px', height: '48px', borderRadius: '12px' }}
                                                    placeholder="Number"
                                                    value={p.passengerPhone}
                                                    onChange={e => {
                                                        const newP = [...form.passengers];
                                                        newP[idx].passengerPhone = e.target.value;
                                                        setForm({ ...form, passengers: newP });
                                                    }}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                        <label className="md-label">Email Address (Optional)</label>
                                        <div style={{ position: 'relative' }}>
                                            <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                                <Icon d={icons.loader} size={16} /> {/* Using loader as generic email-ish icon if no mail icon */}
                                            </div>
                                            <input
                                                type="email"
                                                className="md-input"
                                                style={{ paddingLeft: '40px', height: '48px', borderRadius: '12px' }}
                                                placeholder="passenger@example.com"
                                                value={p.passengerEmail}
                                                onChange={e => {
                                                    const newP = [...form.passengers];
                                                    newP[idx].passengerEmail = e.target.value;
                                                    setForm({ ...form, passengers: newP });
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {/* Add another passenger button */}
                            <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'center' }}>
                                <button
                                    type="button"
                                    onClick={() => setForm({ ...form, passengers: [...form.passengers, { passengerName: '', passengerPhone: '', passengerEmail: '' }] })}
                                    style={{
                                        background: '#e6f0fa', color: '#16539C', border: '1px dashed #00B2A9', borderRadius: '12px', padding: '10px 20px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s'
                                    }}
                                >
                                    + Add Another Passenger
                                </button>
                            </div>

                            {/* Location Section */}
                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <label className="md-label">Pickup Point*</label>
                                <LocationAutocomplete
                                    icon={icons.mapPin}
                                    iconColor="#00B2A9"
                                    borderColor="#e6f0fa"
                                    placeholder="Search pickup location..."
                                    value={form.pickupLocation}
                                    onChange={val => setForm({ ...form, pickupLocation: val })}
                                    required
                                />
                            </div>

                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <label className="md-label">Drop Point*</label>
                                <LocationAutocomplete
                                    icon={icons.mapPin}
                                    iconColor="#ef4444"
                                    borderColor="#fee2e2"
                                    placeholder="Search destination..."
                                    value={form.dropLocation}
                                    onChange={val => setForm({ ...form, dropLocation: val })}
                                    required
                                />
                            </div>

                            {/* Timing Selectors */}
                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <label className="md-label">Booking Date*</label>
                                <input
                                    type="date"
                                    className="md-input"
                                    style={{ height: '48px', borderRadius: '12px' }}
                                    value={form.tripDate}
                                    onChange={e => setForm({ ...form, tripDate: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="md-form-group">
                                <label className="md-label">Pickup Time*</label>
                                <input
                                    type="time"
                                    className="md-input"
                                    style={{ height: '48px', borderRadius: '12px' }}
                                    value={form.pickupTime}
                                    onChange={e => setForm({ ...form, pickupTime: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="md-form-group">
                                <label className="md-label">Drop Time (Optional)</label>
                                <input
                                    type="time"
                                    className="md-input"
                                    style={{ height: '48px', borderRadius: '12px' }}
                                    value={form.dropTime}
                                    onChange={e => setForm({ ...form, dropTime: e.target.value })}
                                />
                            </div>

                            {/* Assign Section */}
                            <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', padding: '12px', background: '#f0f9ff', borderRadius: '12px', border: '1px solid #bae6fd' }}>
                                <input
                                    type="checkbox"
                                    id="vendorToggle"
                                    checked={form.isVendorMode}
                                    onChange={e => setForm({ ...form, isVendorMode: e.target.checked, vendorId: '', vehicleId: '', driverId: '' })}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <label htmlFor="vendorToggle" style={{ fontSize: '14px', fontWeight: 600, color: '#0369a1', cursor: 'pointer' }}>
                                    Assign to External Vendor?
                                </label>
                            </div>

                            {form.isVendorMode ? (
                                <>
                                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                        <label className="md-label">Select Vendor*</label>
                                        <select
                                            className="md-input"
                                            style={{ height: '48px', borderRadius: '12px' }}
                                            value={form.vendorId}
                                            onChange={e => setForm({ ...form, vendorId: e.target.value, vehicleId: '' })}
                                            required
                                        >
                                            <option value="">-- Choose Vendor --</option>
                                            {vendors.map(v => (
                                                <option key={v._id} value={v._id}>{v.vendorName}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                        <label className="md-label">Vendor Vehicle*</label>
                                        <select
                                            className="md-input"
                                            style={{ height: '48px', borderRadius: '12px' }}
                                            value={form.vehicleId}
                                            onChange={e => setForm({ ...form, vehicleId: e.target.value })}
                                            required
                                            disabled={!form.vendorId}
                                        >
                                            <option value="">-- Select Vendor Fleet --</option>
                                            {filteredVendorFleet.map(v => (
                                                <option key={v._id} value={v._id}>{v.registrationNumber} - {v.make}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="md-form-group">
                                        <label className="md-label">Select Vehicle*</label>
                                        <select
                                            className="md-input"
                                            style={{ height: '48px', borderRadius: '12px' }}
                                            value={form.vehicleId}
                                            onChange={e => handleVehicleChange(e.target.value)}
                                            required
                                        >
                                            <option value="">-- Select Vehicle --</option>
                                            {vehicles.map(v => (
                                                <option key={v._id} value={v._id}>
                                                    {v.plateNumber || v.registrationNumber}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="md-form-group">
                                        <label className="md-label">Assign Driver*</label>
                                        <select
                                            className="md-input"
                                            style={{ height: '48px', borderRadius: '12px' }}
                                            value={form.driverId}
                                            onChange={e => setForm({ ...form, driverId: e.target.value })}
                                            required
                                        >
                                            <option value="">-- Select Driver --</option>
                                            {drivers.map(d => (
                                                <option key={d._id} value={d._id}>{d.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}

                            {form.vehicleId && !form.isVendorMode && (
                                <div style={{ gridColumn: 'span 2', display: 'flex', gap: '15px', padding: '15px', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Driver</div>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>
                                            {drivers.find(d => d._id === form.driverId)?.name || 'Unassigned'}
                                        </div>
                                    </div>
                                    <div style={{ width: '1px', background: '#e2e8f0' }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Vendor</div>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>
                                            {vendors.find(v => v._id === form.vendorId)?.vendorName || 'Self-Owned'}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Trip Amount Section */}
                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <label className="md-label">Trip Amount / Fare</label>
                                <div style={{ position: 'relative' }}>
                                    <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#10b981', fontWeight: 700 }}>
                                        {currencySymbol}
                                    </div>
                                    <input
                                        type="number"
                                        className="md-input"
                                        style={{ paddingLeft: currencySymbol.length > 1 ? '60px' : '35px', height: '48px', borderRadius: '12px' }}
                                        placeholder="0.00"
                                        value={form.amount}
                                        onChange={e => setForm({ ...form, amount: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="md-save-btn"
                            disabled={loading}
                            style={{
                                width: '100%', padding: '16px', fontSize: '16px', fontWeight: 700,
                                marginTop: '30px', borderRadius: '16px',
                                background: 'linear-gradient(135deg, #16539C, #7c3aed)',
                                boxShadow: '0 10px 20px rgba(22, 83, 156, 0.2)',
                                display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px',
                                border: 'none', cursor: 'pointer', transition: 'all 0.3s ease'
                            }}
                        >
                            {loading ? (
                                <div className="spinner" style={{ width: 20, height: 20, border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                            ) : "Confirm Adhoc Booking"}
                        </button>
                    </form>
                </div>

                {/* Right: History Card */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div className="md-panel" style={{ padding: '0', overflow: 'hidden', borderRadius: '24px', border: '1px solid #f1f5f9' }}>
                        <div style={{ padding: '20px 25px', borderBottom: '1px solid #f1f5f9', background: '#fff' }}>
                            <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                                <button 
                                    onClick={() => setActiveTab('adhoc')}
                                    style={{ padding: '8px 16px', borderRadius: '8px', fontWeight: 600, fontSize: '14px', border: 'none', cursor: 'pointer', transition: 'all 0.2s', background: activeTab === 'adhoc' ? '#00B2A9' : '#f1f5f9', color: activeTab === 'adhoc' ? '#fff' : '#64748b' }}
                                >
                                    Operator Trips
                                </button>
                                <button 
                                    onClick={() => setActiveTab('client')}
                                    style={{ padding: '8px 16px', borderRadius: '8px', fontWeight: 600, fontSize: '14px', border: 'none', cursor: 'pointer', transition: 'all 0.2s', background: activeTab === 'client' ? '#00B2A9' : '#f1f5f9', color: activeTab === 'client' ? '#fff' : '#64748b' }}
                                >
                                    Client Requests
                                    {clientRequests.length > 0 && <span style={{ marginLeft: '8px', background: activeTab === 'client' ? '#fff' : '#ef4444', color: activeTab === 'client' ? '#00B2A9' : '#fff', padding: '2px 6px', borderRadius: '10px', fontSize: '11px' }}>{clientRequests.length}</span>}
                                </button>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#063A5C', margin: 0 }}>
                                        {activeTab === 'adhoc' ? 'Recent Adhoc Trips' : 'Client Trip Requests'}
                                    </h2>
                                    <p style={{ fontSize: '13px', color: '#94a3b8', margin: '4px 0 0 0' }}>
                                        {activeTab === 'adhoc' ? 'Newly created immediate bookings.' : 'Trips requested by clients waiting for approval/pricing.'}
                                    </p>
                                </div>
                                <span style={{ fontSize: '11px', fontWeight: 700, background: '#eef2ff', color: '#00B2A9', padding: '4px 10px', borderRadius: '20px' }}>
                                    {Math.min(5, displayedTrips.length)} of {displayedTrips.length}
                                </span>
                            </div>
                        </div>

                        <div style={{ maxHeight: '650px', overflowY: 'auto', padding: '10px' }}>
                            {displayedTrips.length === 0 ? (
                                <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                                    <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px' }}>
                                        <Icon d={icons.truck} size={24} strokeWidth={1} />
                                    </div>
                                    <p style={{ color: '#94a3b8', fontSize: '14px' }}>{activeTab === 'adhoc' ? 'No adhoc trips recorded yet.' : 'No client requests found.'}</p>
                                </div>
                            ) : (
                                [...displayedTrips]
                                    .sort((a, b) => new Date(b.createdAt || b.tripDate) - new Date(a.createdAt || a.tripDate))
                                    .slice(0, 5)
                                    .map((trip, idx) => (
                                        <div key={trip._id} style={{
                                            padding: '16px', borderRadius: '16px', marginBottom: '10px',
                                            background: '#fff', border: '1px solid #f1f5f9',
                                            display: 'flex', flexDirection: 'column', gap: '8px',
                                            transition: 'transform 0.2s', cursor: 'default'
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <div>
                                                    <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>{trip.passengerName}</div>
                                                    {trip.clientId && (
                                                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#0369a1', background: '#e0f2fe', display: 'inline-block', padding: '2px 6px', borderRadius: '4px', marginTop: '4px' }}>
                                                            {trip.clientId?.companyName || clients.find(c => c._id === trip.clientId)?.companyName || 'Corporate Client'}
                                                        </div>
                                                    )}
                                                    {trip.passengerEmail && (
                                                        <div style={{ fontSize: '11px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                                                            <Icon d={icons.loader} size={10} /> {trip.passengerEmail}
                                                        </div>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', gap: '5px' }}>
                                                    <div style={{ fontSize: '10px', fontWeight: 700, color: '#00B2A9', background: '#eef2ff', padding: '4px 8px', borderRadius: '20px' }}>
                                                        P: {trip.pickupTime || trip.startTime}
                                                    </div>
                                                    {trip.dropTime && (
                                                        <div style={{ fontSize: '10px', fontWeight: 700, color: '#be185d', background: '#fbcfe8', padding: '4px 8px', borderRadius: '20px' }}>
                                                            D: {trip.dropTime}
                                                        </div>
                                                    )}
                                                    <div style={{
                                                        fontSize: '10px', fontWeight: 800,
                                                        background: getStatusStyle(trip.status).bg,
                                                        color: getStatusStyle(trip.status).color,
                                                        padding: '4px 10px', borderRadius: '20px',
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        {getStatusStyle(trip.status).label}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Step 1: Operator proposes price */}
                                            {trip.status === 'Requested' && (
                                                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                                    <button
                                                        onClick={() => setShowPriceModal({ ...trip, proposedPrice: '', operatorNotes: '' })}
                                                        style={{
                                                            flex: 1, padding: '8px', borderRadius: '10px',
                                                            background: 'linear-gradient(135deg, #00B2A9, #16539C)', color: '#fff', border: 'none',
                                                            fontWeight: 700, fontSize: '13px', cursor: 'pointer'
                                                        }}
                                                    >
                                                        💰 Propose Price
                                                    </button>
                                                    <button
                                                        onClick={() => handleUpdateStatus(trip._id, 'Rejected')}
                                                        style={{
                                                            flex: 1, padding: '8px', borderRadius: '10px',
                                                            background: '#ef4444', color: '#fff', border: 'none',
                                                            fontWeight: 700, fontSize: '13px', cursor: 'pointer'
                                                        }}
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            )}

                                            {/* Client counter-offer: show suggestion note + re-propose */}
                                            {trip.status === 'ClientSuggested' && (
                                                <div style={{ marginTop: '10px' }}>
                                                    <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px', padding: '10px', marginBottom: '10px' }}>
                                                        <div style={{ fontSize: '11px', fontWeight: 800, color: '#92400e', marginBottom: '4px' }}>💬 CLIENT SUGGESTION</div>
                                                        <div style={{ fontSize: '13px', color: '#78350f' }}>{trip.clientNotes || 'Client has a counter-offer.'}</div>
                                                        <div style={{ fontSize: '11px', color: '#b45309', marginTop: '4px' }}>Original proposed: {currencySymbol}{trip.proposedPrice}</div>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button
                                                            onClick={() => setShowPriceModal({ ...trip, proposedPrice: trip.proposedPrice || '', operatorNotes: '' })}
                                                            style={{ flex: 1, padding: '7px', borderRadius: '9px', background: '#00B2A9', color: '#fff', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                                                        >Re-Propose Price</button>
                                                        <button
                                                            onClick={() => handleUpdateStatus(trip._id, 'ClientAccepted', { clientResponse: 'Accept' })}
                                                            style={{ flex: 1, padding: '7px', borderRadius: '9px', background: '#10b981', color: '#fff', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                                                        >Accept As-Is</button>
                                                        <button
                                                            onClick={() => handleUpdateStatus(trip._id, 'Rejected')}
                                                            style={{ flex: 1, padding: '7px', borderRadius: '9px', background: '#ef4444', color: '#fff', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                                                        >Reject</button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Step 2: Client accepted → operator assigns fleet & driver */}
                                            {trip.status === 'ClientAccepted' && (
                                                <div style={{ marginTop: '10px' }}>
                                                    <div style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '10px', marginBottom: '10px' }}>
                                                        <div style={{ fontSize: '11px', fontWeight: 800, color: '#065f46' }}>✅ CLIENT ACCEPTED</div>
                                                        <div style={{ fontSize: '13px', color: '#047857', fontWeight: 600 }}>Price: {currencySymbol}{(trip.proposedPrice || trip.amount || 0).toLocaleString()}</div>
                                                        {trip.operatorNotes && <div style={{ fontSize: '12px', color: '#059669', marginTop: '4px' }}>Notes: {trip.operatorNotes}</div>}
                                                    </div>
                                                    <button
                                                        onClick={() => setShowAssignModal({ 
                                                            ...trip, 
                                                            isVendorMode: trip.isVendorMode || false, 
                                                            vehicleId: trip.vehicleId?._id || trip.vehicleId || '', 
                                                            driverId: trip.driverId?._id || trip.driverId || '', 
                                                            vendorId: trip.vendorId?._id || trip.vendorId || '' 
                                                        })}
                                                        style={{ width: '100%', padding: '9px', borderRadius: '10px', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
                                                    >
                                                        🚌 Assign Fleet & Driver
                                                    </button>
                                                </div>
                                            )}

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#00B2A9' }} />
                                                <div style={{ fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                                                    {trip.pickupLocation}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }} />
                                                <div style={{ fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                                                    {trip.dropLocation}
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', paddingTop: '8px', borderTop: '1px dashed #f1f5f9' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#94a3b8' }}>
                                                    <Icon d={icons.phone} size={11} /> {trip.passengerPhone}
                                                </div>
                                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#063A5C' }}>
                                                    {trip.amount > 0 ? formatCurrency(trip.amount, currencyCountry) :
                                                        (trip.proposedPrice > 0 || trip.status === 'PriceProposed') ? formatCurrency(trip.proposedPrice || 0, currencyCountry) : '—'}
                                                </div>
                                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#063A5C' }}>
                                                    {trip.vehicleId?.plateNumber || trip.vehicleId?.registrationNumber || 'Vehicle Assigned'}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                            )}
                        </div>
                        {adhocTrips.length > 5 && (
                            <div style={{ padding: '14px 20px', borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
                                <button
                                    onClick={() => setShowAllTrips(prev => !prev)}
                                    style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: '#00B2A9', fontSize: '13px', fontWeight: 700,
                                        display: 'flex', alignItems: 'center', gap: '6px', margin: '0 auto'
                                    }}
                                >
                                    {showAllTrips ? '▲ Hide All Trips' : `▼ View All ${adhocTrips.length} Adhoc Trips`}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── All Adhoc Trips Table ── */}
            {showAllTrips && (
                <div style={{ marginTop: '30px' }}>
                    <div style={{ background: '#fff', borderRadius: '24px', border: '1px solid #f1f5f9', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.04)' }}>
                        <div style={{ padding: '22px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#063A5C', margin: 0 }}>All Adhoc Trips</h2>
                                <p style={{ fontSize: '13px', color: '#94a3b8', margin: '4px 0 0 0' }}>Complete list of all on-demand bookings.</p>
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 700, background: '#eef2ff', color: '#00B2A9', padding: '5px 14px', borderRadius: '20px' }}>
                                {adhocTrips.length} Total
                            </span>
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #f1f5f9' }}>
                                        {['#', 'Passenger', 'Phone', 'Pickup → Drop', 'Date', 'Time', 'Amount', 'Status', 'Actions'].map(h => (
                                            <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...displayedTrips]
                                        .sort((a, b) => new Date(b.createdAt || b.tripDate) - new Date(a.createdAt || a.tripDate))
                                        .map((trip, idx) => {
                                            const st = getStatusStyle(trip.status);
                                            return (
                                                <tr key={trip._id} style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.15s' }}
                                                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                                                    <td style={{ padding: '12px 16px', color: '#94a3b8', fontWeight: 600 }}>{idx + 1}</td>
                                                    <td style={{ padding: '12px 16px', fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap' }}>
                                                        {trip.passengerName}
                                                        {trip.passengerEmail && <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 400 }}>{trip.passengerEmail}</div>}
                                                        {trip.clientId && (
                                                            <div style={{ fontSize: '10px', fontWeight: 700, color: '#0369a1', background: '#e0f2fe', display: 'inline-block', padding: '2px 6px', borderRadius: '4px', marginTop: '4px' }}>
                                                                {trip.clientId?.companyName || clients.find(c => c._id === trip.clientId)?.companyName || 'Corporate Client'}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '12px 16px', color: '#475569', whiteSpace: 'nowrap' }}>{trip.passengerPhone}</td>
                                                    <td style={{ padding: '12px 16px', maxWidth: '260px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                                            <div style={{ marginTop: '3px' }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#00B2A9', display: 'inline-block' }} /></div>
                                                            <div style={{ fontSize: '12px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '220px' }}>{trip.pickupLocation}</div>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '3px' }}>
                                                            <div style={{ marginTop: '3px' }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} /></div>
                                                            <div style={{ fontSize: '12px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '220px' }}>{trip.dropLocation}</div>
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '12px 16px', color: '#475569', whiteSpace: 'nowrap' }}>
                                                        {trip.tripDate ? new Date(trip.tripDate).toLocaleDateString() : '—'}
                                                    </td>
                                                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                                                        <span style={{ background: '#eef2ff', color: '#00B2A9', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>P: {trip.pickupTime || trip.startTime || '—'}</span>
                                                        {trip.dropTime && <span style={{ background: '#fbcfe8', color: '#be185d', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, marginLeft: '4px' }}>D: {trip.dropTime}</span>}
                                                    </td>
                                                    <td style={{ padding: '12px 16px', fontWeight: 700, color: '#063A5C', whiteSpace: 'nowrap' }}>
                                                        {trip.amount > 0 ? formatCurrency(trip.amount, currencyCountry) :
                                                            trip.proposedPrice > 0 ? formatCurrency(trip.proposedPrice, currencyCountry) : '—'}
                                                    </td>
                                                    <td style={{ padding: '12px 16px' }}>
                                                        <span style={{ background: st.bg, color: st.color, padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                                                            {st.label}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                                                        {trip.status === 'Requested' && (
                                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                                <button onClick={() => setShowPriceModal({ ...trip, proposedPrice: '', operatorNotes: '' })}
                                                                    style={{ padding: '5px 10px', borderRadius: '8px', background: '#00B2A9', color: '#fff', border: 'none', fontWeight: 600, fontSize: '11px', cursor: 'pointer' }}>💰 Price</button>
                                                                <button onClick={() => handleUpdateStatus(trip._id, 'Rejected')}
                                                                    style={{ padding: '5px 10px', borderRadius: '8px', background: '#ef4444', color: '#fff', border: 'none', fontWeight: 600, fontSize: '11px', cursor: 'pointer' }}>Reject</button>
                                                            </div>
                                                        )}
                                                        {trip.status === 'ClientAccepted' && (
                                                            <button onClick={() => setShowAssignModal({ 
                                                                ...trip, 
                                                                isVendorMode: trip.isVendorMode || false, 
                                                                vehicleId: trip.vehicleId?._id || trip.vehicleId || '', 
                                                                driverId: trip.driverId?._id || trip.driverId || '', 
                                                                vendorId: trip.vendorId?._id || trip.vendorId || '' 
                                                            })}
                                                                style={{ padding: '5px 12px', borderRadius: '8px', background: '#10b981', color: '#fff', border: 'none', fontWeight: 600, fontSize: '11px', cursor: 'pointer' }}>🚌 Assign</button>
                                                        )}
                                                        {trip.status === 'ClientSuggested' && (
                                                            <button onClick={() => setShowPriceModal({ ...trip, proposedPrice: trip.proposedPrice || '', operatorNotes: '' })}
                                                                style={{ padding: '5px 12px', borderRadius: '8px', background: '#f59e0b', color: '#fff', border: 'none', fontWeight: 600, fontSize: '11px', cursor: 'pointer' }}>Re-Propose</button>
                                                        )}
                                                        {!['Requested', 'ClientAccepted', 'ClientSuggested'].includes(trip.status) && (
                                                            <span style={{ color: '#cbd5e1', fontSize: '12px' }}>—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* ── MODAL 1: Operator proposes price ── */}
            {showPriceModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', padding: '32px', borderRadius: '24px', width: '480px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)' }}>
                        <h3 style={{ margin: '0 0 6px 0', fontSize: '20px', fontWeight: 800, color: '#063A5C' }}>💰 Propose Trip Price</h3>
                        <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#64748b' }}>Set a price and optional notes — the client will review and respond.</p>

                        <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '14px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>Passenger:</span>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b' }}>{showPriceModal.passengerName}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>Route:</span>
                                <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>{showPriceModal.pickupLocation?.slice(0, 25)}... ➔ {showPriceModal.dropLocation?.slice(0, 25)}...</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>Date:</span>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: '#00B2A9' }}>{showPriceModal.tripDate ? new Date(showPriceModal.tripDate).toLocaleDateString() : '—'} {showPriceModal.pickupTime || showPriceModal.startTime || ''}</span>
                            </div>
                        </div>

                        <div className="md-form-group" style={{ marginBottom: '16px' }}>
                            <label className="md-label" style={{ fontSize: '13px', fontWeight: 700, color: '#374151' }}>Trip Price *</label>
                            <div style={{ position: 'relative' }}>
                                <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#10b981', fontWeight: 800, fontSize: '14px' }}>
                                    {currencySymbol}
                                </div>
                                <input
                                    type="number"
                                    className="md-input"
                                    style={{ paddingLeft: currencySymbol.length > 1 ? '55px' : '30px', height: '48px', borderRadius: '12px', fontSize: '16px', fontWeight: 700 }}
                                    placeholder="0.00"
                                    value={showPriceModal.proposedPrice}
                                    onChange={e => setShowPriceModal({ ...showPriceModal, proposedPrice: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="md-form-group" style={{ marginBottom: '24px' }}>
                            <label className="md-label" style={{ fontSize: '13px', fontWeight: 700, color: '#374151' }}>Operator Notes (Optional)</label>
                            <textarea
                                className="md-input"
                                rows={3}
                                style={{ resize: 'none', padding: '12px', borderRadius: '12px', fontSize: '13px', lineHeight: 1.5 }}
                                placeholder="Add any notes for the client (inclusions, conditions, etc.)"
                                value={showPriceModal.operatorNotes}
                                onChange={e => setShowPriceModal({ ...showPriceModal, operatorNotes: e.target.value })}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={() => setShowPriceModal(null)} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#fff', fontWeight: 600, cursor: 'pointer', color: '#6b7280' }}>Cancel</button>
                            <button
                                onClick={async () => {
                                    if (!showPriceModal.proposedPrice) { alert('Please enter a price.'); return; }
                                    await handleUpdateStatus(showPriceModal._id, 'PriceProposed', {
                                        proposedPrice: Number(showPriceModal.proposedPrice),
                                        operatorNotes: showPriceModal.operatorNotes,
                                        clientResponse: ''
                                    });
                                    setShowPriceModal(null);
                                }}
                                style={{ flex: 2, padding: '12px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #00B2A9, #16539C)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '14px' }}
                            >
                                📨 Send Price to Client
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── MODAL 2: Assign fleet & driver (after client accepted) ── */}
            {showAssignModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', padding: '30px', borderRadius: '24px', width: '450px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                        <h3 style={{ margin: '0 0 6px 0', fontSize: '20px', fontWeight: 800, color: '#065f46' }}>🚌 Assign Fleet & Driver</h3>
                        <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b' }}>Client accepted the price. Now confirm fleet assignment to schedule the trip.</p>

                        <div style={{ background: '#ecfdf5', padding: '12px', borderRadius: '12px', border: '1px solid #6ee7b7', marginBottom: '18px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <span style={{ fontSize: '12px', color: '#047857' }}>Passenger:</span>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: '#065f46' }}>{showAssignModal.passengerName}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '12px', color: '#047857' }}>Agreed Price:</span>
                                <span style={{ fontSize: '12px', fontWeight: 800, color: '#059669' }}>{currencySymbol}{showAssignModal.proposedPrice}</span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', padding: '10px', background: '#f0f9ff', borderRadius: '10px', border: '1px solid #bae6fd' }}>
                            <input type="checkbox" id="assignVendorToggle" checked={showAssignModal.isVendorMode}
                                onChange={e => setShowAssignModal({ ...showAssignModal, isVendorMode: e.target.checked, vendorId: '', vehicleId: '', driverId: '' })}
                                style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                            <label htmlFor="assignVendorToggle" style={{ fontSize: '13px', fontWeight: 600, color: '#0369a1', cursor: 'pointer' }}>Assign to External Vendor?</label>
                        </div>

                        {showAssignModal.isVendorMode ? (
                            <>
                                <div className="md-form-group" style={{ marginBottom: '14px' }}>
                                    <label className="md-label">Select Vendor*</label>
                                    <select className="md-input" style={{ height: '44px', borderRadius: '10px' }}
                                        value={showAssignModal.vendorId} onChange={e => setShowAssignModal({ ...showAssignModal, vendorId: e.target.value, vehicleId: '' })}>
                                        <option value="">-- Choose Vendor --</option>
                                        {vendors.map(v => <option key={v._id} value={v._id}>{v.vendorName}</option>)}
                                    </select>
                                </div>
                                <div className="md-form-group" style={{ marginBottom: '14px' }}>
                                    <label className="md-label">Vendor Vehicle*</label>
                                    <select className="md-input" style={{ height: '44px', borderRadius: '10px' }}
                                        value={showAssignModal.vehicleId} onChange={e => setShowAssignModal({ ...showAssignModal, vehicleId: e.target.value })} disabled={!showAssignModal.vendorId}>
                                        <option value="">-- Select Vendor Fleet --</option>
                                        {vendorFleet.filter(vf => vf.vendorId?._id === showAssignModal.vendorId || vf.vendorId === showAssignModal.vendorId).map(v => (
                                            <option key={v._id} value={v._id}>{v.registrationNumber} - {v.make}</option>
                                        ))}
                                    </select>
                                </div>
                            </>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                                <div className="md-form-group">
                                    <label className="md-label">Vehicle*</label>
                                    <select className="md-input" style={{ height: '44px', borderRadius: '10px' }}
                                        value={showAssignModal.vehicleId}
                                        onChange={e => {
                                            const vId = e.target.value;
                                            const v = vehicles.find(v => v._id === vId);
                                            setShowAssignModal({ ...showAssignModal, vehicleId: vId, driverId: v?.driverId?._id || v?.driverId || showAssignModal.driverId });
                                        }}>
                                        <option value="">-- Select --</option>
                                        {vehicles.map(v => <option key={v._id} value={v._id}>{v.plateNumber || v.registrationNumber}</option>)}
                                    </select>
                                </div>
                                <div className="md-form-group">
                                    <label className="md-label">Driver*</label>
                                    <select className="md-input" style={{ height: '44px', borderRadius: '10px' }}
                                        value={showAssignModal.driverId} onChange={e => setShowAssignModal({ ...showAssignModal, driverId: e.target.value })}>
                                        <option value="">-- Select --</option>
                                        {drivers.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                            <button onClick={() => setShowAssignModal(null)} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#fff', fontWeight: 600, cursor: 'pointer', color: '#6b7280' }}>Cancel</button>
                            <button
                                onClick={() => handleUpdateStatus(showAssignModal._id, 'Scheduled', {
                                    vehicleId: showAssignModal.vehicleId,
                                    driverId: showAssignModal.driverId,
                                    vendorId: showAssignModal.vendorId,
                                    isVendorMode: showAssignModal.isVendorMode,
                                    amount: showAssignModal.proposedPrice
                                })}
                                disabled={!showAssignModal.vehicleId || (!showAssignModal.isVendorMode && !showAssignModal.driverId)}
                                style={{ flex: 2, padding: '12px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '14px', opacity: (showAssignModal.vehicleId && (showAssignModal.isVendorMode || showAssignModal.driverId)) ? 1 : 0.5 }}
                            >
                                ✅ Confirm & Schedule
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
                .adhoc-grid {
                    display: grid;
                    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
                    gap: 30px;
                    align-items: start;
                }
                @media (max-width: 1024px) {
                    .adhoc-grid {
                        grid-template-columns: 1fr;
                        gap: 20px;
                    }
                }
                .md-form-group { display: flex; flex-direction: column; gap: 8px; }
                .md-input:focus { border-color: #00B2A9 !important; box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1) !important; outline: none; }
                .md-save-btn:hover { transform: translateY(-2px); box-shadow: 0 15px 30px rgba(22, 83, 156, 0.3); opacity: 0.95; }
                .md-save-btn:active { transform: translateY(1px); }
                input[type=number]::-webkit-inner-spin-button, 
                input[type=number]::-webkit-outer-spin-button { 
                    -webkit-appearance: none; 
                    margin: 0; 
                }
                input[type=number] {
                    -moz-appearance: textfield;
                }
            `}</style>
        </div>
    );
}

